// client/src/pages/seo/QrCodeMenuPrintingEn.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "before", label: "What must be ready before printing" },
  { id: "materials", label: "Table tents, cards, stickers or holders?" },
  { id: "design", label: "Size, contrast and readability" },
  { id: "placement", label: "Where should the QR code be placed?" },
  { id: "test", label: "Printing checklist before ordering" },
  { id: "faq", label: "QR code printing FAQ" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuPrintingEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title = "Print a QR Code for a Menu: Size & Placement | Best Place";
    setMeta(
      "description",
      "Print a QR code for a restaurant menu: practical guidance on table tents, cards, stickers, contrast, placement and scan testing before printing."
    );
    setCanonical("https://best-place.club/en/qr-code-menu-printing");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/qr-code-menu-printing",
          de: "/de/qr-code-speisekarte-drucken",
          no: "/no/qr-kode-meny-utskrift",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>Print a QR Code for a Restaurant Menu: Material, Size and Placement</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Before ordering table tents, cards or stickers, the digital
                destination page should be finished and the QR code should work
                reliably on real smartphones.
              </p>
              <p className="seoHero__text">
                This page covers only the physical use of the QR code: print
                material, readability, placement and testing. For the digital QR
                menu itself, see our main page.
              </p>
              <p><Link to="/en/qr-code-menu-for-restaurants">Go to QR code menus for restaurants</Link></p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Contents</h2>
        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="before" className="acquaintance">
          <h2>What must be ready before printing</h2>
          <p>
            The most common mistake is ordering attractive print materials before
            it is clear where the QR code actually leads. First, the menu page,
            destination URL and guest view should work. Only then does it make sense
            to produce larger quantities.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">The menu URL is stable and publicly accessible.</li>
            <li className="text__seo-li">The menu works on several smartphones.</li>
            <li className="text__seo-li">The QR code has been tested from the final design.</li>
            <li className="text__seo-li">Content updates do not require replacing the printed code.</li>
          </ul>
        </section>

        <section id="materials" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Table tents, cards, stickers or holders?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Table tent</strong>
                <p>Easy to see and easy to replace. Practical for restaurants with fixed tables.</p>
              </div>
              <div>
                <strong>Small table card</strong>
                <p>Affordable for tests and seasonal changes before choosing a permanent material.</p>
              </div>
              <div>
                <strong>Sticker</strong>
                <p>Saves space, but is less flexible if the design or placement needs to change later.</p>
              </div>
              <div>
                <strong>Wooden or 3D-printed holder</strong>
                <p>Can look premium, but should only be produced after a successful real-world test.</p>
              </div>
            </div>
            <p>
              For the specific topic of table tents, see our additional page{" "}
              <Link to="/en/restaurant-qr-code-table-tent">Restaurant QR Code Table Tent</Link>.
            </p>
          </div>
        </section>

        <section id="design" className="acquaintance">
          <h2>Size, contrast and readability</h2>
          <p>
            QR codes should not be designed at the theoretical minimum size. In a
            restaurant, distance, light, reflections, cameras and viewing angle all
            change the scanning situation. In practice, a slightly larger,
            high-contrast code is usually more reliable than a very small,
            decorative one.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">high contrast between the code and background</li>
            <li className="text__seo-li">enough clear space around the code</li>
            <li className="text__seo-li">no strong reflections across the code</li>
            <li className="text__seo-li">text next to it explaining what will open</li>
            <li className="text__seo-li">scan test directly from the final print file</li>
          </ul>
          <p>
            A clear message such as “Scan the menu” or “Open menu without an app”
            removes uncertainty about what will happen after the scan.
          </p>
        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Where should the QR code be placed?</h2>
            <p>
              The best place is where the guest actually needs the menu. On a
              restaurant table, that usually means clearly visible without forcing
              the guest to actively search for it.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">table tent or table card</li>
              <li className="text__seo-li">counter or self-service area</li>
              <li className="text__seo-li">window or entrance for information before entering</li>
              <li className="text__seo-li">flyers and takeaway packaging for later access</li>
            </ul>
            <p>
              Avoid positions that are constantly covered by glasses, dishes or
              decoration.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>Printing checklist before ordering</h2>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Open and check the final menu URL.</li>
            <li className="text__seo-li">Scan the QR code from the final print file.</li>
            <li className="text__seo-li">Test with iPhone and Android.</li>
            <li className="text__seo-li">Test from a typical seated distance.</li>
            <li className="text__seo-li">Test under normal restaurant lighting.</li>
            <li className="text__seo-li">Produce a small test batch first.</li>
          </ol>
          <p>
            Only after these points work should you order larger quantities or
            produce more elaborate holders.
          </p>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>QR code printing FAQ</h2>
            <div className="seoFaq">
              <details>
                <summary>Do I need to reprint the QR code every time the menu changes?</summary>
                <p>No, as long as the code points to a stable menu URL and only the content behind it changes.</p>
              </details>
              <details>
                <summary>What is better: a sticker or a table tent?</summary>
                <p>Table tents and replaceable cards are more flexible. Stickers are permanent, but harder to change later.</p>
              </details>
              <details>
                <summary>Can I use a 3D-printed holder?</summary>
                <p>Yes. A holder for a replaceable card with a cleanly printed QR code is a practical option.</p>
              </details>
              <details>
                <summary>Should I remove printed menus immediately?</summary>
                <p>Not necessarily. Many restaurants use digital and printed menus in parallel.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Related topics</h2>
          <div className="seoCompareSmall">
            <p><Link to="/en/qr-code-menu-for-restaurants">QR code menu for restaurants</Link></p>
            <p><Link to="/en/restaurant-qr-code-table-tent">QR code table tent</Link></p>
            <p><Link to="/en/qr-code-menu-without-app">QR menu without an app</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
